/**
 * Expense category prediction hook — two PURE tiers. Neither touches
 * the chip: scheduling (debounce, idle timer, flush, trust gate, dedup,
 * cancel-before-apply) and application live in core's
 * `useCategoryPredictionScheduler`, which the form drives with these
 * two functions. Mirrors mobile's `predictCategoryFromTitleLive` /
 * `predictCategoryFromTitleCloud`.
 *
 *   `predictCategory` — the FREE tier. Title length < 3 → null;
 *   keyword tier (~5 ms, deterministic, no I/O) when a probe matched
 *   above `minConfidence`; otherwise an `other-fallback` placeholder the
 *   form holds back until the paid tier concludes.
 *
 *   `predictCategoryCloud` — the PAID tier. Claude Haiku behind a
 *   DynamoDB cache via `/expenses/predict-category`; ~$0.00075 on a
 *   cache miss. Returns a `CloudTierOutcome`: `no-answer` covers the
 *   gates — unauthenticated (no bearer token), `VITE_ENABLE_CLOUD_LLM`
 *   not "true", smart suggestions turned off — a below-floor answer,
 *   and an answer from a different taxonomy than ours (the server
 *   validates against ITS taxonomy, which skews from ours while API
 *   and client versions roll out); `failed` is a transport/server
 *   failure the scheduler may retry.
 *
 * Dev-mode logging mirrors mobile's: `[CategoryPrediction:<tier>]`
 * tags in the console with timing + final pick so you can see which
 * tier produced the chip and how fast (stripped in prod via
 * `import.meta.env.DEV`).
 */
import { useCallback, useEffect, useRef } from 'react';

import {
  ApiClientService,
  CLOUD_MIN_CONFIDENCE,
  type CloudTierOutcome as CoreCloudTierOutcome,
  EXPENSE_TAXONOMY_VERSION,
  type ExpenseCategory,
  isValidExpensePrediction,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  CategoryPredictionSource,
  PredictCategoryResponseDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useApi } from '@/hooks/useApi';

import {
  MIN_TITLE_CHARS,
  expenseCategoryService,
} from '@/services/categoryPrediction/ExpenseCategoryService';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

interface UseCategoryPredictionOptions {
  /** Keyword matches below this confidence fall through to `other`. */
  minConfidence?: number;
}

type PredictionSource =
  'keyword' | 'cloud' | 'too-short' | 'anonymous' | 'other-fallback';

/**
 * The free tier's answer for a title. The shared scheduler applies it
 * and decides from its `source`/`confidence` whether the paid tier is
 * worth firing.
 */
export interface LivePrediction {
  category: ExpenseCategory;
  confidence: number;
  subcategory?: { type: string; confidence: number };
  source: Extract<CategoryPredictionSource, 'keyword' | 'other-fallback'>;
}

/** A confident cloud answer, ready for the caller to apply. */
export interface CloudPredictionResult {
  category: ExpenseCategory;
  confidence: number;
  subcategory?: { type: string; confidence: number };
  source: Extract<CategoryPredictionSource, 'cloud'>;
}

/** The paid tier's outcome, specialised to web's cloud result shape. */
export type CloudTierOutcome = CoreCloudTierOutcome<CloudPredictionResult>;

const NO_ANSWER: CloudTierOutcome = { status: 'no-answer' };
const FAILED: CloudTierOutcome = { status: 'failed' };

/**
 * Dev-only path tracer — labels in the browser console so it's
 * obvious which tier produced the chip and how long each tier took.
 * Stripped in prod via `import.meta.env.DEV`. Mirrors mobile's
 * `logPath` for cross-platform diagnosability.
 */
function logPath(
  source: PredictionSource,
  title: string,
  result?: {
    category: ExpenseCategory;
    confidence: number;
    subcategory?: { type: string; confidence: number };
  } | null,
  reason?: string,
  durationMs?: number
) {
  if (!import.meta.env.DEV) return;
  const tag = `[CategoryPrediction:${source}]`;
  const timing =
    typeof durationMs === 'number' ? ` [${durationMs.toFixed(0)}ms]` : '';
  if (result) {
    const sub = result.subcategory ? `/${result.subcategory.type}` : '';

    console.log(
      `${tag} "${title}" → ${result.category}${sub} (${result.confidence.toFixed(2)})${timing}`
    );
  } else {
    console.log(
      `${tag} "${title}" → no prediction${reason ? ` (${reason})` : ''}${timing}`
    );
  }
}

export function useCategoryPrediction({
  minConfidence = 0.4,
}: UseCategoryPredictionOptions = {}) {
  const { i18n } = useTranslation();
  const user = useAuthStore(state => state.user);
  const api = useApi();

  // The cloud-tier call needs `api` and `user`, but those references
  // can flip identity across renders (useApi's memo depends on
  // multiple useCallbacks; the user-typing flow re-renders the
  // hook's host while the form's debounce timers are in flight). If
  // either ends up in the `useCallback` deps below, the callbacks
  // get a new identity every render — the consumer effects then keep
  // clearing their timers before they fire, and even the synchronous
  // keyword tier never runs. Stash them in refs so callback identity
  // is governed only by genuinely stable values. Syncing happens in
  // an effect (not during render) to satisfy `react-hooks/refs`; the
  // debounce in the consumer means both callbacks always see
  // post-commit values.
  const apiRef = useRef(api);
  const userRef = useRef(user);
  useEffect(() => {
    apiRef.current = api;
    userRef.current = user;
  });

  /** Free tier: length floor → keyword → `other`. Never hits the network. */
  const predictCategory = useCallback(
    async (title: string): Promise<LivePrediction | null> => {
      const trimmed = title.trim();
      if (trimmed.length < MIN_TITLE_CHARS) {
        logPath('too-short', trimmed, null, `length=${trimmed.length}`);
        return null;
      }

      const kwStartedAt = performance.now();
      // App language first, the browser's second: the keyword tier tries
      // them in this order and the other vocabularies after.
      const keyword = expenseCategoryService.predictFromKeyword(trimmed, [
        i18n.language,
        typeof navigator === 'undefined' ? undefined : navigator.language,
      ]);
      const kwElapsed = performance.now() - kwStartedAt;
      if (keyword && keyword.confidence >= minConfidence) {
        logPath('keyword', trimmed, keyword, undefined, kwElapsed);
        return {
          category: keyword.category,
          confidence: keyword.confidence,
          subcategory: keyword.subcategory,
          source: 'keyword',
        };
      }

      // Placeholder: the form does not show it as a chip; it tells the
      // scheduler to consult the paid tier.
      logPath(
        'other-fallback',
        trimmed,
        { category: 'other', confidence: 1.0 },
        'keyword missed'
      );
      return { category: 'other', confidence: 1.0, source: 'other-fallback' };
    },
    [i18n.language, minConfidence]
  );

  /**
   * Paid tier: one call to `/expenses/predict-category`. Pure with
   * respect to the form — it never emits; see `CloudTierOutcome`.
   */
  const predictCategoryCloud = useCallback(
    async (title: string): Promise<CloudTierOutcome> => {
      const trimmed = title.trim();
      if (trimmed.length < MIN_TITLE_CHARS) {
        logPath('too-short', trimmed, null, `length=${trimmed.length}`);
        return NO_ANSWER;
      }

      // Gated by VITE_ENABLE_CLOUD_LLM in apps/web/.env — defaults to
      // disabled, staging/production env files set it to "true"
      // explicitly. Keeps Anthropic spend at zero during local dev
      // unless the developer opts in for testing the cloud path.
      if (import.meta.env.VITE_ENABLE_CLOUD_LLM !== 'true') {
        logPath(
          'cloud',
          trimmed,
          null,
          'disabled (VITE_ENABLE_CLOUD_LLM is not "true")'
        );
        return NO_ANSWER;
      }
      // User-level opt-out (Settings → smart category suggestions).
      // When off, nothing leaves the device — keyword tier only.
      if (!useUIStore.getState().cloudPredictionEnabled) {
        logPath('cloud', trimmed, null, 'disabled by user setting');
        return NO_ANSWER;
      }
      // Endpoint requires a bearer token.
      if (!userRef.current) {
        logPath('anonymous', trimmed, null, 'no bearer token to attach');
        return NO_ANSWER;
      }

      // Cloud cache keys on the full BCP-47 locale tag — mobile
      // sends `Localization.getLocales()[0]?.languageTag` (e.g.
      // 'en-CA', 'es-CO'). Web's `i18n.language` is just the UI
      // language code ('en', 'es') because i18n only ships those
      // five bare-code translations. Sending the UI code here
      // would route web to a different cache bucket than mobile
      // and miss every row the mobile app has populated. Read
      // `navigator.language` (the device locale) so web hits the
      // same rows. Fall back to `i18n.language` in environments
      // without `navigator` (SSR, tests).
      const cloudLocale =
        typeof navigator !== 'undefined' && navigator.language
          ? navigator.language
          : i18n.language;
      const startedAt = performance.now();
      let data: PredictCategoryResponseDto;
      try {
        const response = (await ApiClientService.expenses.predictCategory(
          apiRef.current,
          {
            title: trimmed,
            locale: cloudLocale,
            taxonomyVersion: EXPENSE_TAXONOMY_VERSION,
          }
        )) as SuccessResponseDto<PredictCategoryResponseDto>;
        data = response.data;
      } catch (error) {
        // Network failure, 503 (not configured / Anthropic degraded),
        // 502 (hallucinated key), or 409 (a newer request from this
        // user superseded us server-side).
        logPath(
          'cloud',
          trimmed,
          null,
          `request failed: ${(error as Error)?.message ?? 'unknown'}`,
          performance.now() - startedAt
        );
        return FAILED;
      }
      const elapsed = performance.now() - startedAt;

      if (data.confidence < CLOUD_MIN_CONFIDENCE) {
        logPath(
          'cloud',
          trimmed,
          null,
          `confidence below floor (${data.confidence.toFixed(2)})`,
          elapsed
        );
        return NO_ANSWER;
      }
      const subKey = data.subcategory.trim() ? data.subcategory : null;
      const taxonomyMismatch =
        data.taxonomyVersion !== EXPENSE_TAXONOMY_VERSION
          ? `taxonomy version ${data.taxonomyVersion} != ${EXPENSE_TAXONOMY_VERSION}`
          : !isValidExpensePrediction(data.category, subKey)
            ? `invalid taxonomy match (${data.category}/${data.subcategory || 'none'})`
            : null;
      if (taxonomyMismatch) {
        logPath('cloud', trimmed, null, taxonomyMismatch, elapsed);
        return NO_ANSWER;
      }
      const prediction: CloudPredictionResult = {
        category: data.category as ExpenseCategory,
        confidence: data.confidence,
        subcategory: subKey
          ? { type: subKey, confidence: data.confidence }
          : undefined,
        source: 'cloud',
      };
      logPath('cloud', trimmed, prediction, `source=${data.source}`, elapsed);
      return { status: 'answered', prediction };
    },
    [i18n.language]
  );

  return { predictCategory, predictCategoryCloud };
}
